// Read-only profile view for a persona — reachable from the chat list and the
// chat header. Mirrors the SwipeCard's visual so users see the same details
// they saw when they first matched, just without the drag affordance.

import { useEffect } from 'react';
import { X, GraduationCap, MessageCircle } from 'lucide-react';
import type { Persona } from '@/lib/types';
import { Portrait } from '@/components/portraits';
import { C } from '@/styles/tokens';

interface PersonaProfileSheetProps {
  persona: Persona;
  onClose: () => void;
  /**
   * When provided, the sheet renders a "Start chatting" CTA at the bottom.
   * Used by Browse mode to convert the read-only profile into an entry point
   * to chat. Existing callers (chat list portrait, chat header tap) leave
   * this off so the sheet stays read-only there.
   */
  onStartChat?: () => void;
}

export function PersonaProfileSheet({ persona, onClose, onStartChat }: PersonaProfileSheetProps) {
  // Close on Escape. The backdrop click handler covers pointer dismissal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal
      aria-label={`${persona.en} profile`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 anim-backdrop-in"
        style={{ background: 'rgba(26,22,20,0.55)' }}
        aria-hidden
        onClick={onClose}
      />

      <div
        className="relative w-full max-w-[420px] max-h-[92dvh] rounded-[28px] overflow-hidden shadow-2xl flex flex-col ff-body anim-scale-in"
        style={{ background: C.cream }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close profile"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.85)', color: C.ink }}
        >
          <X size={18} aria-hidden />
        </button>

        <div className="relative" style={{ height: 280, flexShrink: 0 }}>
          <Portrait persona={persona} />
          <div
            className="absolute inset-x-0 bottom-0 h-28 pointer-events-none"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}
            aria-hidden
          />
          <div className="absolute bottom-3 left-5 right-5 text-white">
            <div className="flex items-baseline gap-2.5">
              <span className="ff-zh-serif text-3xl font-bold tracking-tight leading-none">
                {persona.name}
              </span>
              <span className="ff-display text-2xl italic opacity-90 leading-none">
                {persona.age}
              </span>
            </div>
            <div className="ff-body text-[11px] tracking-wider opacity-90 mt-1">
              {persona.pinyin} · {persona.cityEn}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pt-3 pb-5 flex flex-col gap-2.5">
          <div className="flex items-center justify-between gap-2">
            <div
              className="text-[10px] uppercase tracking-[0.18em] flex-1 truncate"
              style={{ color: C.inkLight }}
            >
              {persona.occupation}
            </div>
            <div
              className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider"
              style={{ background: C.blush, color: C.vermillionDeep }}
            >
              <GraduationCap size={10} aria-hidden />
              {persona.level.tag}
            </div>
          </div>

          <div
            className="ff-zh-serif text-[17px] leading-snug font-medium"
            style={{ color: C.ink }}
          >
            &ldquo;{persona.bio}&rdquo;
          </div>

          <div className="ff-body text-[12px] leading-relaxed" style={{ color: C.inkLight }}>
            {persona.intro}
          </div>

          <div className="mt-2">
            <div
              className="ff-body text-[9px] uppercase tracking-[0.25em] mb-1.5"
              style={{ color: C.inkSofter }}
            >
              ◆ Expect to learn
            </div>
            <div className="flex flex-wrap gap-1.5">
              {persona.topics.map((t) => (
                <div
                  key={t.hz}
                  className="flex items-baseline gap-1.5 px-2.5 py-1 rounded-full"
                  style={{ background: 'white', border: `1px solid ${C.blush}` }}
                >
                  <span
                    className="ff-zh-serif text-[14px] font-medium"
                    style={{ color: C.ink }}
                  >
                    {t.hz}
                  </span>
                  <span className="ff-body text-[10px] opacity-60" style={{ color: C.ink }}>
                    {t.py}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {onStartChat && (
            <button
              type="button"
              onClick={onStartChat}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full ff-body text-sm font-semibold text-white"
              style={{ background: persona.accent }}
            >
              <MessageCircle size={16} aria-hidden />
              <span className="ff-zh-serif text-[15px]">开始聊天</span>
              <span className="opacity-90">· Start chatting</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
